import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUser, googleAuth, facebookAuth } from '../utils/api';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { FiEye, FiEyeOff, FiLock, FiMail } from 'react-icons/fi';
import toast from 'react-hot-toast';
import styles from './AuthPage.module.css';

// Replace with your actual Google Client ID from console.cloud.google.com
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Replace with your Facebook App ID from developers.facebook.com
const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

export default function LoginPage() {
  const [form, setForm]         = useState({ email: '', password: '' });
  const [loading, setLoading]   = useState(false);
  const [fbLoading, setFbLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { login }   = useAuth();
  const navigate    = useNavigate();
  const location    = useLocation();
  const redirect    = location.state?.from || '/';

  const afterLogin = (data) => {
    login(data);
    toast.success(`Welcome back, ${data.name}! 👋`);
    if (['editor', 'moderator', 'admin'].includes(data.role)) {
      navigate('/admin');
    } else {
      navigate(redirect);
    }
  };

  // Email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await loginUser(form);
      afterLogin(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  // Google login success
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { data } = await googleAuth({ token: credentialResponse.credential });
      afterLogin(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Google login failed');
    }
  };

  // Facebook login
  const handleFacebookLogin = () => {
    if (!FB_APP_ID) {
      toast.error('Facebook login not configured yet');
      return;
    }
    setFbLoading(true);
    window.FB?.login(
      async (response) => {
        if (response.authResponse) {
          try {
            const { data } = await facebookAuth({
              accessToken: response.authResponse.accessToken,
              userID:      response.authResponse.userID,
            });
            afterLogin(data);
          } catch (err) {
            toast.error(err.response?.data?.message || 'Facebook login failed');
          }
        } else {
          toast.error('Facebook login cancelled');
        }
        setFbLoading(false);
      },
      { scope: 'public_profile,email' }
    );
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className={styles.page}>
        <div className={styles.card}>

          {/* Header */}
          <div className={styles.header}>
            <Link to="/" className={styles.logo}>🛒 BD<strong>Shop</strong></Link>
            <h1 className={styles.heading}>Welcome Back</h1>
            <p className={styles.subheading}>Sign in to your account to continue</p>
          </div>

          {/* OAuth Buttons */}
          <div className={styles.oauthSection}>
            {/* Google */}
            {GOOGLE_CLIENT_ID ? (
              <div className={styles.googleWrap}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error('Google login failed')}
                  text="signin_with"
                  shape="rectangular"
                  theme="outline"
                  size="large"
                  width="100%"
                  locale="en"
                />
              </div>
            ) : (
              <div className={styles.oauthNotice}>
                <span>💡 Add REACT_APP_GOOGLE_CLIENT_ID to .env to enable Google login</span>
              </div>
            )}

            {/* Facebook */}
            <button
              type="button"
              className={styles.fbBtn}
              onClick={handleFacebookLogin}
              disabled={fbLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              {fbLoading ? 'Connecting…' : 'Continue with Facebook'}
            </button>
          </div>

          {/* Divider */}
          <div className={styles.dividerRow}>
            <span className={styles.dividerLine} />
            <span className={styles.dividerText}>or sign in with email</span>
            <span className={styles.dividerLine} />
          </div>

          {/* Email/password form */}
          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="login-email">Email Address</label>
              <div className={styles.inputWrap}>
                <div className={styles.inputIcon}><FiMail size={16} /></div>
                <input
                  id="login-email" type="email"
                  className={`form-input ${styles.inputWithIcon}`}
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="you@example.com"
                  autoComplete="email" required
                />
              </div>
            </div>

            <div className="form-group">
              <div className={styles.labelRow}>
                <label className="form-label" htmlFor="login-password">Password</label>
                <Link to="/forgot-password" className={styles.forgotLink}>Forgot password?</Link>
              </div>
              <div className={styles.inputWrap}>
                <div className={styles.inputIcon}><FiLock size={16} /></div>
                <input
                  id="login-password"
                  type={showPass ? 'text' : 'password'}
                  className={`form-input ${styles.inputWithIcon}`}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Enter your password"
                  autoComplete="current-password" required
                  style={{ paddingRight: 44 }}
                />
                <button type="button" className={styles.eyeBtn} onClick={() => setShowPass(s => !s)}>
                  {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={`btn btn-primary btn-lg btn-block ${styles.submitBtn}`}
              disabled={loading}
            >
              {loading ? <><div className="spinner spinner-sm" /> Signing in…</> : 'Sign In'}
            </button>
          </form>

          <div className={styles.formFooter}>
            <p>Don't have an account? <Link to="/register" className={styles.authLink}>Create account</Link></p>
          </div>
        </div>

        <div className={styles.trustBadges}>
          <span>🔒 Secure Login</span>
          <span>🇧🇩 Bangladesh-based</span>
          <span>💳 bKash & Nagad</span>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}
